import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Box, ConfirmationModal, DropdownMenu, DropdownMenuItem, IconButton, Row, Typography, useOverlayContext } from 'seagull';
import { ChevronDown, ChevronUp, DragHandle, Edit, Kebab, Trash } from 'seagull/icons';
import { BookmarkAdd } from 'seagull/icons/content-edit';
import { getTestIds } from 'seagull/utils/TestIds';
import { DisplayScopeCheckbox } from '~/components/DisplayScopeCheckbox/DisplayScopeCheckbox';
import { useLocaleKeys } from '~/i18n';
import { isUrlValid } from '~/utils/videoUrl/videoUrl';
import s from './CustomPageCard.module.scss';
import { EditTitlePopover } from './EditTitlePopover/EditTitlePopover';
import { PdfPageContent } from './PdfPageContent/PdfPageContent';
import { SaveAsTemplateModal } from './SaveAsTemplateModal/SaveAsTemplateModal';
import { TextPageContent } from './TextPageContent/TextPageContent';
import { VideoPageContent } from './VideoPageContent/VideoPageContent';
export const GetCustomPageCardTID = (parentTestId)=>getTestIds(parentTestId);
const isContentInvalid = (page)=>{
    switch(page.type){
        case 'text':
            return page.text.textId === '';
        case 'pdf':
            return page.pdf.pdfName === '';
        case 'video':
            return !isUrlValid(page.video.videoUrl);
    }
};
export const CustomPageCard = (param)=>{
    let { page, isReadOnly, initialExpanded, submitAttempted, onTitleChange, onRemove, onPageUpdate, onDisplayScopeChange, isDragActive, dragHandle, testId } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetCustomPageCardTID(testId), [
        testId
    ]);
    const { showModal } = useOverlayContext();
    const [expanded, setExpanded] = useState(!!initialExpanded);
    const [editAnchorEl, setEditAnchorEl] = useState(null);
    const isEditPopoverOpen = editAnchorEl != null;
    const toggleExpanded = useCallback(()=>{
        setExpanded((prev)=>!prev);
    }, []);
    const handleEditClick = useCallback((e)=>{
        e.stopPropagation();
        setEditAnchorEl(e.currentTarget);
    }, []);
    const handleEditPopoverClose = useCallback(()=>{
        setEditAnchorEl(null);
    }, []);
    const handleRemoveClick = useCallback(()=>{
        showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    skin: "danger",
                    title: t.customPages.removePageConfirmTitle(),
                    content: t.customPages.removePageConfirmMessage(),
                    primaryButtonLabel: t.customPages.remove(),
                    secondaryButtonLabel: t.common.buttons.cancel(),
                    onPrimaryButtonClick: ()=>{
                        onRemove === null || onRemove === void 0 ? void 0 : onRemove(page.id);
                        dismissModal();
                    },
                    onSecondaryButtonClick: ()=>dismissModal(),
                    onCloseButtonClick: ()=>dismissModal()
                });
            }
        });
    }, [
        onRemove,
        page.id,
        showModal,
        t
    ]);
    const handleSaveAsTemplateClick = useCallback(()=>{
        showModal({
            biName: 'save_as_custom_page_template_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(SaveAsTemplateModal, {
                    page: page,
                    dismissModal: dismissModal
                });
            }
        });
    }, [
        page,
        showModal
    ]);
    const handleDisplayScopeToggle = useCallback(()=>{
        const newScope = page.displayScope === 'proposalAndAgreement' ? 'proposalOnly' : 'proposalAndAgreement';
        onDisplayScopeChange === null || onDisplayScopeChange === void 0 ? void 0 : onDisplayScopeChange(newScope);
    }, [
        onDisplayScopeChange,
        page.displayScope
    ]);
    const showError = !isReadOnly && !!submitAttempted && isContentInvalid(page);
    const cardClassName = "".concat(s.card).concat(showError ? " ".concat(s.cardError) : '').concat(!expanded ? " ".concat(s.collapsed) : '');
    const isDisplayScopeVisible = page.type !== 'video';
    const isDisplayScopeChecked = page.displayScope === 'proposalAndAgreement';
    const showDisplayScope = isDisplayScopeVisible && (!isReadOnly || isDisplayScopeChecked);
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.CustomPageCardTestIds.Root(),
        className: cardClassName,
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                testId: testIds.CustomPageCardTestIds.Header(),
                className: s.header,
                alignItems: "center",
                onClick: toggleExpanded,
                children: [
                    !isReadOnly && /*#__PURE__*/ _jsx(Box, {
                        testId: testIds.CustomPageCardTestIds.DragHandle(),
                        className: s.dragHandleSlot,
                        children: dragHandle !== null && dragHandle !== void 0 ? dragHandle : /*#__PURE__*/ _jsx(DragHandle, {
                            size: 16
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Row, {
                        className: "".concat(s.titleArea).concat(!isReadOnly && !isDragActive ? " ".concat(s.editable) : ''),
                        alignItems: "center",
                        onClick: !isReadOnly && !isDragActive ? handleEditClick : undefined,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                testId: testIds.CustomPageCardTestIds.Title(),
                                variant: "h5",
                                weight: "normal",
                                noWrap: true,
                                children: page.pageTitle
                            }),
                            !isReadOnly && !isDragActive && /*#__PURE__*/ _jsx(Row, {
                                className: s.editIconButton,
                                testId: testIds.CustomPageCardTestIds.PencilButton(),
                                children: /*#__PURE__*/ _jsx(Edit, {
                                    size: 20
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Row, {
                        className: s.headerActions,
                        alignItems: "center",
                        children: [
                            !isReadOnly && !isDragActive && /*#__PURE__*/ _jsx(_Fragment, {
                                children: /*#__PURE__*/ _jsxs(DropdownMenu, {
                                    target: /*#__PURE__*/ _jsx(IconButton, {
                                        testId: testIds.CustomPageCardTestIds.KebabButton(),
                                        onClick: (e)=>e.stopPropagation(),
                                        children: /*#__PURE__*/ _jsx(Kebab, {})
                                    }),
                                    children: [
                                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                            testId: testIds.CustomPageCardTestIds.SaveAsTemplateAction(),
                                            onClick: handleSaveAsTemplateClick,
                                            iconStart: /*#__PURE__*/ _jsx(BookmarkAdd, {
                                                size: 20
                                            }),
                                            children: t.customPages.saveAsTemplate()
                                        }),
                                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                            testId: testIds.CustomPageCardTestIds.RemoveAction(),
                                            onClick: handleRemoveClick,
                                            skin: "error",
                                            iconStart: /*#__PURE__*/ _jsx(Trash, {
                                                size: 20
                                            }),
                                            children: t.customPages.remove()
                                        })
                                    ]
                                })
                            }),
                            !isReadOnly && !isDragActive && /*#__PURE__*/ _jsx(Box, {
                                className: s.headerDivider
                            }),
                            !isDragActive && /*#__PURE__*/ _jsx(IconButton, {
                                testId: testIds.CustomPageCardTestIds.ChevronButton(),
                                onClick: (e)=>{
                                    e.stopPropagation();
                                    toggleExpanded();
                                },
                                children: expanded ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {})
                            })
                        ]
                    })
                ]
            }),
            expanded && !isDragActive && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(Box, {
                        testId: testIds.CustomPageCardTestIds.ContentArea(),
                        className: s.contentArea,
                        children: [
                            page.type === 'text' && /*#__PURE__*/ _jsx(TextPageContent, {
                                testId: testIds.CustomPageCardTestIds.TextPageContent(),
                                textId: page.text.textId,
                                onTextIdChange: (newTextId)=>onPageUpdate === null || onPageUpdate === void 0 ? void 0 : onPageUpdate(_object_spread_props(_object_spread({}, page), {
                                        text: {
                                            textId: newTextId
                                        }
                                    })),
                                disabled: isReadOnly,
                                submitAttempted: submitAttempted
                            }),
                            page.type === 'pdf' && /*#__PURE__*/ _jsx(PdfPageContent, {
                                testId: testIds.CustomPageCardTestIds.PdfPageContent(),
                                pdfName: page.pdf.pdfName,
                                pdfFileName: page.pdf.pdfFileName,
                                onFileChange: (file)=>onPageUpdate === null || onPageUpdate === void 0 ? void 0 : onPageUpdate(_object_spread_props(_object_spread({}, page), {
                                        pdf: {
                                            pdfName: file.pdfName,
                                            pdfFileName: file.pdfFileName
                                        }
                                    })),
                                disabled: isReadOnly,
                                submitAttempted: submitAttempted
                            }),
                            page.type === 'video' && /*#__PURE__*/ _jsx(VideoPageContent, {
                                testId: testIds.CustomPageCardTestIds.VideoPageContent(),
                                value: page.video.videoUrl,
                                onChange: (url)=>onPageUpdate === null || onPageUpdate === void 0 ? void 0 : onPageUpdate(_object_spread_props(_object_spread({}, page), {
                                        video: {
                                            videoUrl: url
                                        }
                                    })),
                                disabled: isReadOnly,
                                submitAttempted: submitAttempted
                            })
                        ]
                    }),
                    showDisplayScope && /*#__PURE__*/ _jsx(Box, {
                        className: s.displayScopeRow,
                        children: /*#__PURE__*/ _jsx(DisplayScopeCheckbox, {
                            testId: testIds.CustomPageCardTestIds.DisplayScopeCheckbox(),
                            checked: isDisplayScopeChecked,
                            onChange: handleDisplayScopeToggle,
                            disabled: isReadOnly
                        })
                    })
                ]
            }),
            isEditPopoverOpen && /*#__PURE__*/ _jsx(EditTitlePopover, {
                testId: testIds.CustomPageCardTestIds.EditTitlePopover(),
                anchorEl: editAnchorEl,
                initialValue: page.pageTitle,
                onSave: (newTitle)=>onTitleChange === null || onTitleChange === void 0 ? void 0 : onTitleChange(newTitle),
                onClose: handleEditPopoverClose
            })
        ]
    });
};
